import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChartOutlined,
  ExitToAppOutlined,
  SettingsOutlined,
  EventNoteOutlined,
  AppsOutlined,
  ApartmentOutlined,
  LogoutOutlined,
  ShoppingCartOutlined,
  QrCodeScannerOutlined,
  LocationOnOutlined,
  ArrowBackOutlined,
} from "@mui/icons-material";
import { Divider, IconButton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../app/hook";
import { logOut } from "../../auth/slices/authSlice";
import {
  userClientDb,
  userFullName,
  userId,
  uiLanguage,
} from "../../auth/selectors/userSelector";
import { IconField, Alert } from "../../../components";
import { useOptionsListStyles } from "../css";
import {
  useLocalStorage,
  useAuthorizationError,
  useLoader,
} from "../../../hooks";
import { getErrorMessage } from "../../../components/form/formUtils";
import * as Routes from "../../../app/routes";
import { checkForCustomProcedures } from "../../api/parameters";
import DbList from "../../settings/components/DbList";
import Grid from "@mui/material/Grid2";
import { getUserModules, IUserModule } from "../../api/docListsandDetails";

interface IListItem {
  title: string;
  route: string;
  icon: React.ReactNode;
  disabled: boolean;
  errorMessage: string;
}

const modulesMap: Record<
  string,
  Omit<IListItem, "disabled" | "errorMessage">
> = {
  RO: {
    title: "sales",
    route: Routes.ROUTE_SALES.path,
    icon: <BarChartOutlined fontSize="small" />,
  },
  PO: {
    title: "purchases",
    route: Routes.ROUTE_PURCHASES.path,
    icon: <ShoppingCartOutlined fontSize="small" />,
  },
  VD: {
    title: "insideAccounting",
    route: Routes.ROUTE_INTERNAL.path,
    icon: <ExitToAppOutlined fontSize="small" />,
  },
  KP: {
    title: "caches",
    route: Routes.ROUTE_SCANNER.path,
    icon: <QrCodeScannerOutlined fontSize="small" />,
  },
  IF: {
    title: "inventory",
    route: Routes.ROUTE_INVENTORY.path,
    icon: <EventNoteOutlined fontSize="small" />,
  },
  KA: {
    title: "assembly",
    route: Routes.ROUTE_ASSEMBLY.path,
    icon: <ApartmentOutlined fontSize="small" />,
  },
  "0E": {
    title: "locations",
    route: Routes.ROUTE_LOCATION.path,
    icon: <LocationOnOutlined fontSize="small" />,
  },
  PS: {
    title: "informationByBarcode",
    route: Routes.ROUTE_PRODUCT_INFO.path,
    icon: <AppsOutlined fontSize="small" />,
  },
  SETTINGS: {
    title: "settings",
    route: Routes.ROUTE_SETTINGS.path,
    icon: <SettingsOutlined fontSize="small" />,
  },
};

function OptionsList() {
  const { classes } = useOptionsListStyles();
  const { t } = useTranslation();
  const user = useAppSelector(userId);
  const userName = useAppSelector(userFullName);
  const locale = useAppSelector(uiLanguage);
  const userDb = useAppSelector(userClientDb);
  const dispatch = useAppDispatch();
  const [error, setError] = useState({ error: false, message: "", status: 0 });
  const { state: locationState } = useLocation();
  const [logoutUser] = useAuthorizationError();
  const [, setLoader] = useLoader();
  const navigate = useNavigate();
  const [, setIsCustomProc] = useLocalStorage("productCustomProc");
  const [modules, setModules] = useState<IUserModule[]>([]);
  const [localAlert, setLocalAlert] = useState({ open: false, message: "" });

  useEffect(() => {
    if (userDb?.id && user && locale) {
      const params = { clientDbId: userDb.id, user, locale };
      setLoader(true);
      getUserModules(params)
        .then(setModules)
        .catch((err) => {
          const msg = getErrorMessage(err);
          setError({ error: true, message: msg.message, status: msg.status });
        })
        .finally(() => setLoader(false));

      const paramsProc = { clientDbId: userDb.id, locale, user };
      checkForCustomProcedures(paramsProc)
        .then(({ data }) => {
          const customProc = data.find(
            (param: any) =>
              param.procName?.toUpperCase() ===
              "RGI_MSCAN_GET_PRODUCT_INFO_CUSTOM",
          );
          setIsCustomProc(
            !!customProc &&
              (customProc.isCustomProc === 1 ||
                customProc.isCustomProc === "1"),
          );
        })
        .catch((reason) => {
          const errMessage = getErrorMessage(reason);
          setError({
            error: true,
            message: errMessage.message,
            status: errMessage.status,
          });
        })
        .finally(() => setLoader(false));
    }
  }, [userDb?.id, user, locale]);

  const handleCloseButton = () => {
    if (locationState?.from !== Routes.ROUTE_LOGIN.path) navigate(-1);
  };

  const logUserOut = useCallback(() => dispatch(logOut()), [dispatch]);

  const handleCloseError = () => {
    logoutUser(error.status);
    if (error.status !== 401)
      setError({ error: false, message: "", status: 0 });
  };

  const handleItemClick = (item: IListItem) => {
    if (item.disabled && item.errorMessage) {
      setLocalAlert({ open: true, message: `Klaida! ${item.errorMessage}` });
    } else {
      navigate(item.route);
    }
  };

  const items: IListItem[] = modules
    .filter((m) => m.show)
    .sort((a, b) => a.order - b.order)
    .map((m) => {
      const conf = modulesMap[m.module];
      if (!conf) return null;
      return {
        title: t(conf.title, conf.title),
        route: conf.route,
        icon: conf.icon,
        disabled: m.disabled,
        errorMessage: m.disabled ? m.errorMessage : "",
      };
    })
    .filter(Boolean) as IListItem[];

  if (!items.some((i) => i.route === Routes.ROUTE_SETTINGS.path)) {
    const conf = modulesMap.SETTINGS;
    items.push({
      title: t(conf.title, "Nustatymai"),
      route: conf.route,
      icon: conf.icon,
      disabled: false,
      errorMessage: "",
    });
  }

  return (
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      wrap="nowrap"
      className={classes.grid}
    >
      {error.error && (
        <Alert
          isOpen
          message={error.message}
          iconColor="error"
          onClose={handleCloseError}
        />
      )}

      {localAlert.open && (
        <Alert
          isOpen
          message={localAlert.message}
          iconColor="warning"
          onClose={() => setLocalAlert({ open: false, message: "" })}
        />
      )}

      <Grid>
        <Grid
          container
          justifyContent="space-between"
          className={classes.dbList}
        >
          <DbList />
          {locationState?.from && (
            <IconButton size="medium" onClick={handleCloseButton}>
              <ArrowBackOutlined />
            </IconButton>
          )}
        </Grid>

        <Divider variant="fullWidth" className={classes.divider} />

        <Grid
          container
          direction="column"
          wrap="nowrap"
          className={classes.listItems}
        >
          {items.map((opt) => (
            <Grid key={opt.route}>
              <div
                className={classes.link}
                style={{ cursor: opt.disabled ? "default" : "pointer" }}
                onClick={() => handleItemClick(opt)}
              >
                <IconField
                  title={opt.title}
                  icon={opt.icon}
                  disabled={opt.disabled}
                  greenText={opt.route === locationState?.from}
                />
              </div>
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
        className={classes.logout}
      >
        <div className={classes.userName}>{userName}</div>
        <IconField
          title={t("logout", "Atsijungti")}
          icon={<LogoutOutlined fontSize="small" />}
          onClick={logUserOut}
          noMargin
        />
      </Grid>
    </Grid>
  );
}

export default React.memo(OptionsList);
